<template>
  <div class="flex flex-row md:justify-center gap-x-4 px-2">
    <div class="font-bold mt-1">
      {{ index }}.
    </div>
    <div class="sm:col-span-2 grid grid-cols-2 gap-x-2 gap-y-2 w-full">
      <div class="col-span-2 relative">
        <div
          ref="dateButton"
          class="pseudo-select hover:ring focus:ring hover:ring-neutral-100"
          @click="open = !open"
          @keydown="open = !open"
        >
          <span>{{ displayDate }}</span>
          <fa :icon="['fal', 'chevron-down']" />
        </div>
        <div
          ref="dayPicker"
          class="relative"
        >
          <vetster-day-picker
            v-show="open"
            :date="today"
            :disabled-dates="disabledDates"
            class="w-max absolute top-1 left-0 right-0 shadow-lg z-10 bg-white p-2 rounded-lg border border-neutral-300"
            @change="setDateFrom"
          />
        </div>
      </div>
      <div class="col-span-1">
        <vetster-select
          ref="fromPicker"
          v-model.trim="inputs.time_from"
          :options="fromOptions"
          :placeholder="$t('app.from')"
          class="[&]:[&_select]:rounded-md"
          @change="focusOnTimeTo"
        />
      </div>
      <div class="col-span-1">
        <vetster-select
          ref="toPicker"
          v-model.trim="inputs.time_to"
          :options="toOptions"
          :placeholder="$t('app.to')"
          class="[&]:[&_select]:rounded-md"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import filter from 'lodash/filter';
import { DateTime } from 'luxon';

import VetsterDayPicker from '@/components/VetsterDayPicker';
import VetsterSelect from '@/components/VetsterSelect';
import { i18n } from '@/i18n';
import ClickAway from '@/mixins/ClickAway';

library.add(faChevronDown);

export default {
  name: 'VetsterInquiryTimeslotPicker',

  components: {
    VetsterDayPicker,
    VetsterSelect,
  },

  mixins: [ClickAway],

  props: {
    disabledDates: {
      type: Array,
      default: () => [],
    },
    index: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      open: false,
      inputs: {
        date_from: null,
        time_from: null,
        time_to: null,
      },
    };
  },

  computed: {
    displayDate() {
      return this.inputs.date_from ?? this.$t('search.select-date');
    },
    fromOptions() {
      return filter(this.getTimeOptions, (time) => {
        if (!this.inputs.time_to && this.inputs.time_to !== 0) {
          return true;
        }

        return this.inputs.time_to > time.value;
      });
    },
    getTimeOptions() {
      const options = [];
      let newDate = DateTime.local().startOf('day').set({ hour: 9 });

      while (newDate <= DateTime.local().startOf('day').set({ hour: 17 })) {
        options.push({
          value: Number(
            newDate
              .setLocale(i18n.locale)
              .toLocaleString({
                hour: '2-digit',
                minute: '2-digit',
                hourCycle: 'h23',
              })
              .match(/\d|\.|-/g)
              .join(''),
          ),
          text: newDate
            .setLocale(i18n.locale)
            .toLocaleString(DateTime.TIME_SIMPLE),
        });

        if (newDate.plus({ minutes: 30 }) > DateTime.local().startOf('day').set({ hour: 17 })) {
          newDate = newDate.plus({ minutes: 29 });
        } else {
          newDate = newDate.plus({ minutes: 30 });
        }
      }

      return options;
    },
    today() {
      return DateTime.local();
    },
    toOptions() {
      return filter(this.getTimeOptions, (time) => {
        if (!this.inputs.time_from && this.inputs.time_from !== 0) {
          return true;
        }

        return this.inputs.time_from < time.value;
      });
    },
  },

  watch: {
    inputs: {
      deep: true,
      handler(data) {
        this.$emit('update', data);
      },
    },
  },

  created() {
    document.addEventListener('click', this.registerClickAway);
  },

  methods: {
    focusOnTimeTo() {
      this.$refs.toPicker?.focus();
    },
    registerClickAway(event) {
      this.hideOnClickAway(
        event,
        this.$refs.dateButton,
        () => { this.open = false; },
        this.$refs.dayPicker,
      );
    },
    setDateFrom(dateFrom) {
      this.inputs.date_from = dateFrom.ymd;
      this.open = false;
      this.$refs.fromPicker?.focus();
    },
  },
};
</script>
